import React, { memo, useCallback, useState } from 'react';
import styled from 'styled-components';

import {
  INPUT_MULTILINE_HEIGHT,
  INPUT_PADDING_HORIZONTAL,
  getInputColors,
} from '@front/common/src/components/input/styles';

import { StylelessTextarea } from '../utils';
import { WithClassNameProps } from '../types';

import { Container, InputContainer, InputFrame, baseInputStyle, InputStyleProps } from './components';
import BottomMessage from './components/bottom-message';
import InputIcon from './components/input-icon';
import AnimatedPlaceholder from './components/animated-placeholder';

const StyledInputContainer = styled(InputContainer)`
  position: relative;
  flex-grow: 1;
  padding-right: 0;
`;

const StyledTextarea = styled(StylelessTextarea)<InputStyleProps>`
  ${baseInputStyle};
  min-height: ${INPUT_MULTILINE_HEIGHT}px;
  height: 100%;
`;

const StyledInputIcon = styled(InputIcon)`
  position: absolute;
  top: ${INPUT_PADDING_HORIZONTAL}px;
  right: 0;
`;

export type TextareaProps = WithClassNameProps & {
  name: string;
  label: string;
  value?: string;
  id: string;
  iconName?: string;
  placeholder?: string;
  error?: string;
  message?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  disabled?: boolean;
};

const Textarea = ({
  name,
  label,
  id,
  value,
  iconName,
  placeholder,
  error,
  message,
  onBlur,
  onFocus,
  onChange,
  className,
  rows,
  disabled = false,
}: TextareaProps) => {
  const hasLabel = !!label;
  const hasValue = !!value;
  const [isFocused, setIsFocused] = useState(false);

  const inputColors = getInputColors({ error, hasValue, isFocused });

  const onInputBlur = useCallback(() => {
    setIsFocused(false);
    if (onBlur) onBlur();
  }, [onBlur]);

  const onInputFocus = useCallback(() => {
    setIsFocused(true);
    if (onFocus) onFocus();
  }, [onFocus]);

  const textareaProps = {
    $hasLabel: hasLabel,
    $isFocused: isFocused,
    'aria-label': label,
    $margin: 15,
    disabled,
    error,
    id,
    label,
    name,
    onBlur: onInputBlur,
    onChange,
    onFocus: onInputFocus,
    placeholder: placeholder || ' ',
    rows,
    value,
  };

  return (
    <Container $isDisabled={disabled} className={className}>
      <InputFrame $inputId={id} $hasHoverStyle={!disabled && !isFocused && !error} $inputColors={inputColors}>
        <StyledInputContainer>
          <StyledTextarea {...textareaProps} />
          {hasLabel && (
            <AnimatedPlaceholder
              color={inputColors.placeholder}
              inputId={id}
              isActive={isFocused || !!value}
              label={label}
            />
          )}
        </StyledInputContainer>
        {iconName && <StyledInputIcon color={inputColors.icon} inputId={id} name={iconName} />}
      </InputFrame>
      <BottomMessage error={error} message={message} />
    </Container>
  );
};

export default memo(Textarea);
